import { Injectable } from '@angular/core';
import { ExtendedFuseNavigationItem } from 'app/mock-api/common/navigation/data';
import { PermissionService } from './permission.service';

@Injectable({
    providedIn: 'root',
})
export class NavigationFilterService {
    constructor(private permissionService: PermissionService) {}

    /**
     * Filter navigation items based on user permissions
     * @param navigation - Array of navigation items
     * @returns Filtered navigation array
     */
    filterNavigationByPermissions(
        navigation: ExtendedFuseNavigationItem[]
    ): ExtendedFuseNavigationItem[] {
        return navigation
            .map((item) => this.filterNavigationItem(item))
            .filter((item) => item !== null) as ExtendedFuseNavigationItem[];
    }

    /**
     * Filter a single navigation item and its children
     * @param item - Navigation item to filter
     * @returns Filtered item or null if no access
     */
    private filterNavigationItem(
        item: ExtendedFuseNavigationItem
    ): ExtendedFuseNavigationItem | null {
        // Dashboard is always accessible (moduleId = 1)
        if (item.moduleId === 1) {
            return item;
        }

        // Special case: Reports (moduleId = 27) and Master Data (moduleId = 38)
        // These have single permission flags that control the entire section
        if (item.moduleId === 27 || item.moduleId === 38) {
            const hasPermission = this.permissionService.hasPermission(
                item.moduleId,
                'View'
            );
            // If no permission, hide the entire section including all children
            if (!hasPermission) {
                return null;
            }
            // If has permission, show the entire section with all children
            return item;
        }

        // Check if item has children
        if (item.children && item.children.length > 0) {
            // Special handling for children of Reports and Master Data
            const parentHasSpecialPermission =
                this.hasSpecialPermissionParent(item);
            if (parentHasSpecialPermission) {
                // If parent has special permission and user has access, show all children
                return item;
            }

            // Normal filtering for other sections
            // Filter children recursively
            const filteredChildren = item.children
                .map((child) => this.filterNavigationItem(child))
                .filter(
                    (child) => child !== null
                ) as ExtendedFuseNavigationItem[];

            // If no children have access, hide the parent
            if (filteredChildren.length === 0) {
                return null;
            }

            // Return parent with filtered children
            return {
                ...item,
                children: filteredChildren,
            };
        }

        if (item.anyModuleIds?.length) {
            return item.anyModuleIds.some(id => this.permissionService.hasPermission(id, 'View')) ? item : null;
        }

        // For leaf items, check permission
        if (item.moduleId) {
            const hasPermission = this.permissionService.hasPermission(
                item.moduleId,
                'View'
            );
            return hasPermission ? item : null;
        }

        // If no moduleId specified, show the item (for backwards compatibility)
        return item;
    }

    /**
     * Check if an item is a child of Reports or Master Data sections
     * @param item - Navigation item to check
     * @returns boolean indicating if item is under special permission parent
     */
    private hasSpecialPermissionParent(
        item: ExtendedFuseNavigationItem
    ): boolean {
        // Check if this item is directly Reports or Master Data
        if (item.moduleId === 27 || item.moduleId === 34) {
            return true;
        }

        // Check if this item has children that would be under Reports or Master Data
        // This is for nested structures within these sections
        if (
            item.id === 'business-report' ||
            item.id === 'ticket-management-report' ||
            item.id === 'order-management-report' ||
            item.id === 'plan-report' ||
            item.id === 'inquiries-report' ||
            item.id === 'feedback-report'
        ) {
            return true;
        }

        return false;
    }

    /**
     * Check if user has access to any navigation items
     * @param navigation - Array of navigation items
     * @returns boolean indicating if user has any access
     */
    hasAnyAccess(navigation: ExtendedFuseNavigationItem[]): boolean {
        const filteredNav = this.filterNavigationByPermissions(navigation);
        return filteredNav.length > 0;
    }

    /**
     * Get the first accessible navigation item (for default redirect)
     * @param navigation - Array of navigation items
     * @returns First accessible navigation item or null
     */
    getFirstAccessibleItem(
        navigation: ExtendedFuseNavigationItem[]
    ): ExtendedFuseNavigationItem | null {
        for (const item of navigation) {
            // Dashboard is always accessible
            if (item.moduleId === 1) {
                return item;
            }

            // Check if item has children
            if (item.children && item.children.length > 0) {
                const firstChild = this.getFirstAccessibleItem(item.children);
                if (firstChild) {
                    return firstChild;
                }
            }

            if (item.anyModuleIds?.some(id => this.permissionService.hasPermission(id, 'View'))) return item;

            // For leaf items, check permission
            if (item.moduleId) {
                const hasPermission = this.permissionService.hasPermission(
                    item.moduleId,
                    'View'
                );
                if (hasPermission) {
                    return item;
                }
            }
        }

        return null;
    }

    /**
     * Debug method to log navigation filtering results
     * @param navigation - Original navigation array
     */
    debugNavigationFiltering(navigation: ExtendedFuseNavigationItem[]): void {

        const filtered = this.filterNavigationByPermissions(navigation);

        navigation.forEach((item) => {
            this.debugNavigationItem(item, 0);
        });

    }

    /**
     * Debug helper to log individual navigation item permissions
     */
    private debugNavigationItem(
        item: ExtendedFuseNavigationItem,
        depth: number
    ): void {
        const indent = '  '.repeat(depth);
        const moduleId = item.moduleId || 'N/A';

        let hasPermission: string | boolean = 'N/A';
        let specialCase = '';

        if (item.moduleId) {
            hasPermission = this.permissionService.hasPermission(
                item.moduleId,
                'View'
            );

            // Mark special cases
            if (item.moduleId === 27) {
                specialCase = ' [REPORTS - Single Permission]';
            } else if (item.moduleId === 34) {
                specialCase = ' [MASTER DATA - Single Permission]';
            } else if (this.hasSpecialPermissionParent(item)) {
                specialCase = ' [Child of Special Permission Parent]';
            }
        }

        console.log(
            `${indent}${item.title} (moduleId: ${moduleId}, hasView: ${hasPermission}${specialCase})`
        );

        if (item.children) {
            item.children.forEach((child) => {
                this.debugNavigationItem(child, depth + 1);
            });
        }
    }
}
